import * as React from "react";
import { useContextStateManagement } from "../context/ContextApi";
import {
  resetErrorFieldStyle,
  txtFormatChecker,
  isNumber,
  isValidContactNumber,
} from "../Variables";
import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { logout } from "../features/userSlice";
import { Link, useNavigate } from "react-router-dom";
import { TimeZoneDropdown } from "./TimeZoneDropdown";
import { BusinessHoursPicker } from "./BusinessHoursPicker";
import { MultiSelectDropdown } from "./MultiSelectDropdown";
import { ToolsMultiSelectDropdown } from "./ToolsMultiSelectDropdown";
import { LlmReasoningControls } from "./LlmReasoningControls";
import {
  DEFAULT_REASONING_EFFORT,
  normalizeReasoningSelection,
} from "./llmReasoningOptions";
import "../components/Auth/Auth.css";

const LEADGEN_PROVIDER_ID_STORAGE_KEY = "cm_leadGen_providerId";

// "claude-sonnet-5" also matches Sonnet 5.5. Grok 4.7 is deliberately absent: Bedrock rejects
// explicit cache points for it and caches its prompts automatically.
const supportsBedrockPromptCaching = (model = "") =>
  model.includes("claude-haiku-4-5") ||
  model.includes("claude-haiku-5-5") ||
  model.includes("claude-sonnet-4-6") ||
  model.includes("claude-sonnet-5");

const LEGACY_SONNET_4_MODEL = "us.anthropic.claude-sonnet-4-20250514-v1:0";
const SONNET_4_6_MODEL = "us.anthropic.claude-sonnet-4-6";

const normalizeBedrockModelId = (model = "") =>
  model === LEGACY_SONNET_4_MODEL ? SONNET_4_6_MODEL : model;

const LEAD_PROMPT_STEPS = [
  { id: 1, label: "Step 1", title: "Basic Details" },
  { id: 2, label: "Step 2", title: "Configuration" },
  { id: 3, label: "Step 3", title: "Model & Publish" },
];

export const LeadGenerationOnboardingForm = () => {
  const DEFAULT_MODAL = "us.anthropic.claude-haiku-4-5-20251001-v1:0";
  const DEFAULT_VOICE_PROVIDER = "Deepgram";
  const DEFAULT_VOICE_LABEL = "Har (Female)";
  const DEFAULT_STATUS = "active";
  const BACKGROUND_NOISE_PLACEHOLDER = "__select_background_noise__";
  const NO_BACKGROUND_NOISE = "";
  const DEFAULT_BACKGROUND_NOISE = BACKGROUND_NOISE_PLACEHOLDER;
  const DEFAULT_SIP_TERMINATION_SECONDS = "900";
  const DEFAULT_TRANSFER_SIP_TERMINATION_SECONDS = "900";
  const DEFAULT_OUTBOUND_CALLER_ID_SOURCE = "dedicated";
  const DEFAULT_LANGUAGES = ["English"];
  const {
    PracticeId,
    practiceName,
    token,
    usersPostRequest,
    setToken,
    fetchUserRecord,
    activeTab,
    userType,
    setUserType,
    dateRangeRequest,
    outBoundContact,
    setOutBoundContact,
  } = useContextStateManagement();
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [loading, setLoading] = useState(false);
  const [providerId, setProviderId] = useState("");
  const [providerData, setProviderData] = useState([]);
  const [promptFile, setPromptFile] = useState("");
  const [promptFiledownloadLink, setPromptFiledownloadLink] = useState("");
  const [providerName, setProviderName] = useState("");
  const [providerFacilityName, setProviderFacilityName] = useState("");
  const [providerContactNumber, setProviderContactNumber] = useState("");
  // const [providerSpecialtyType, setProviderSpecialtyType] = useState("");
  // const [patientIssues, setPatientIssues] = useState("");
  // const [additionalOfferedService, setAdditionalOfferedService] = useState("");
  // const [additionalOfferedServiceDetail, setAdditionalOfferedServiceDetail] = useState("");
  // const [betterServicesReason, setBetterServicesReason] = useState("");
  // const [customerWebsite, setCustomerWebsite] = useState("");
  // const [operationalHours, setOperationalHours] = useState("");
  // const [insurancesAccepted, setInsurancesAccepted] = useState("");
  const [selectedVoiceModal, setSelectedVoiceModal] = useState("");
  const [selectedVoice, setSelectedVoice] = useState(DEFAULT_VOICE_PROVIDER);
  const [selectedModal, setSelectedModal] = useState(DEFAULT_MODAL);
  const [firstMessage, setFirstMessage] = useState("");
  const [lastMessage, setLastMessage] = useState("");
  const [status, setStatus] = useState(DEFAULT_STATUS);
  const [parameters, setParameters] = useState([]);
  // const [apiKeys, setApiKeys] = useState("");
  const [voiceModals, setVoiceModals] = useState([]);
  const [selectedVoiceModals, setSelectedVoiceModals] = useState([]);
  const [voices, setVoices] = useState([]);
  const [selectedTimezone, setSelectedTimezone] = useState("");
  const [businessHoursDays, setBusinessHoursDays] = useState([]);
  const [businessHoursStart, setBusinessHoursStart] = useState("");
  const [businessHoursEnd, setBusinessHoursEnd] = useState("");
  const [voiceMail, setVoiceMail] = useState("");
  const [wantsVoiceMail, setWantsVoiceMail] = useState(false);
  const [backgroundNoise, setBackgroundNoise] = useState(
    DEFAULT_BACKGROUND_NOISE
  );
  const [sipTerminationDynamicSeconds, setSipTerminationDynamicSeconds] =
    useState(DEFAULT_SIP_TERMINATION_SECONDS);
  const [transferSipTerminationDynamicSeconds, setTransferSipTerminationDynamicSeconds] =
    useState(DEFAULT_TRANSFER_SIP_TERMINATION_SECONDS);
  const [outboundCallerIdSource, setOutboundCallerIdSource] = useState(
    DEFAULT_OUTBOUND_CALLER_ID_SOURCE
  );
  const [practiceNumbers, setPracticeNumbers] = useState({
    inboundNumber: "",
    outboundNumber: "",
    inboundCallerIdAvailable: false,
    dedicatedOutboundAvailable: false,
  });
  const [cacheEnabled, setCacheEnabled] = useState(false);
  const [greetingInterruptible, setGreetingInterruptible] = useState(false);
  const [cacheTTL, setCacheTTL] = useState(300);
  const [thinkingEnabled, setThinkingEnabled] = useState(false);
  const [reasoningEffort, setReasoningEffort] = useState(DEFAULT_REASONING_EFFORT);
  const [eouMinDelay, setEouMinDelay] = useState(0.3);
  const [eouMaxDelay, setEouMaxDelay] = useState(0.8);
  const [turnDetectionMode, setTurnDetectionMode] = useState("vad");
  const [sttProvider, setSttProvider] = useState("Deepgram");
  const [elevenLabsTtsModel, setElevenLabsTtsModel] = useState("eleven_flash_v2_5");
  const [fluxKeyterms, setFluxKeyterms] = useState("");
  const [multiAgentEnabled, setMultiAgentEnabled] = useState(false);
  const [agentConfigs, setAgentConfigs] = useState([]);
  const [apiFunctions, setApiFunctions] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState(
    DEFAULT_LANGUAGES
  );
  // Intentionally plain useState (not usePersistedState): the step index
  // should always start at "Basic Details" on a fresh visit to Prompt Hub,
  // not resume wherever the user last left off in a previous session.
  const [currentStep, setCurrentStep] = useState(1);
  const restoredProviderRef = useRef(false);
  // const optionsForLanguages = ["English", "Spanish", "Multilingual"];
  const optionsForLanguages = ["English", "Spanish"];
  const selectedNumberSid = "";

  const dispatch = useDispatch();

  // Keep providerContactNumber in sync with the practice outbound number; UI no longer selects it.
  useEffect(() => {
    setProviderContactNumber(outBoundContact || "");
  }, [outBoundContact]);

  // Refresh outbound contact when form mounts or practice changes
  useEffect(() => {
    const refreshOutbound = async () => {
      if (!PracticeId || !token) return;
      const assigned = await fetchUserRecord(
        token,
        `/api/getAssignedPhoneNumbers?practiceId=${PracticeId}`
      );
      if (Array.isArray(assigned)) {
        const outbound = assigned.find(
          (a) =>
            a.assignmentType &&
            a.assignmentType.toLowerCase().includes("outbound")
        );
        if (outbound && outbound.phoneNumber) {
          setOutBoundContact(outbound.phoneNumber);
          setProviderContactNumber(outbound.phoneNumber);
        } else {
          // clear if none assigned
          setOutBoundContact("");
          setProviderContactNumber("");
        }
      }
    };
    refreshOutbound();
  }, [PracticeId, token, fetchUserRecord, setOutBoundContact]);
  const navigate = useNavigate();
  let formData = new FormData();
  let isAnyUploadFieldEmpty = false;
  let isValidDetails = false;

  const getElement = (id) => document.getElementById(id);
  const enableElement = (id) => getElement(id)?.removeAttribute("disabled");
  const disableElement = (id) =>
    getElement(id)?.setAttribute("disabled", "true");
  const setElementValue = (id, value) => {
    const element = getElement(id);
    if (element) {
      element.value = value;
    }
  };
  const addElementClass = (id, className) =>
    getElement(id)?.classList.add(className);

  const formatFluxKeyterms = (value) => {
    if (Array.isArray(value)) {
      return value.join("\n");
    }
    return typeof value === "string" ? value : "";
  };

  // Some stored rows have a JSON-array string ("[\"English\"]") sitting as a
  // single array element instead of a plain language name, from an older
  // write path that double-encoded this field. Unwrap those so the chip
  // shows "English" instead of the literal JSON text.
  const normalizeSelectedLanguages = (value) => {
    if (!Array.isArray(value)) return value;
    return value.flatMap((entry) => {
      if (typeof entry !== "string") return [entry];
      const trimmed = entry.trim();
      if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) return parsed.map((v) => String(v));
        } catch (_) {
          // not valid JSON, keep the original string
        }
      }
      return [entry];
    });
  };
  const isBedrockCacheAvailable = supportsBedrockPromptCaching(selectedModal);

  useEffect(() => {
    if (!isBedrockCacheAvailable && cacheEnabled) {
      setCacheEnabled(false);
    }
  }, [isBedrockCacheAvailable, cacheEnabled]);

  // Keep thinking/effort valid for the selected model (e.g. effort above High is cleared
  // when Claude thinking is turned off; Grok always reasons).
  useEffect(() => {
    const normalized = normalizeReasoningSelection(selectedModal, thinkingEnabled, reasoningEffort);
    if (normalized.thinkingEnabled !== thinkingEnabled) setThinkingEnabled(normalized.thinkingEnabled);
    if (normalized.reasoningEffort !== reasoningEffort) setReasoningEffort(normalized.reasoningEffort);
  }, [selectedModal, thinkingEnabled, reasoningEffort]);

  const handleTimezoneChange = (e) => {
    setSelectedTimezone(e.target.value);
  };

  function resetInputFieldsColor() {
    document
      .querySelectorAll(
        "input.invalid-details, textarea.invalid-details, select.invalid-details, .invalid-details"
      )
      .forEach((element) => {
        element?.classList?.remove("invalid-details");
      });

    const selectedLanguagesElement = document.querySelector(
      ".autocomplete_languages"
    );
    if (selectedLanguagesElement) {
      selectedLanguagesElement.classList.remove("invalid-details");
    }
  }

  const fetchApiHandle = async () => {
    let url = `/api/LeadGenerationOnboarding/${PracticeId}`;
    const userData = await fetchUserRecord(token, url);
    if (userData == "Token is expired, login again") {
      setIsTokenExpired(true);
    } else if (userData && userData.providersData && userData.providersData.length === 0) {
      setProviderData([]);
    } else if (userData && userData.providersData) {
      setProviderData(userData.providersData);
    }
    if (userData && userData.practiceNumbers) {
      setPracticeNumbers(userData.practiceNumbers);
    }
    setApiFunctions(userData && userData.functions ? userData.functions : []);
  };

  const fetchVoiceAndModals = async () => {
    let url = `/api/getVoiceModals`;
    const userData = await fetchUserRecord(token, url);
    if (userData == "Token is expired, login again") {
      setIsTokenExpired(true);
    } else if (userData == "No records") {
      setVoiceModals([]);
    } else {
      filterVoices(userData);
      setVoiceModals(userData);
    }
  };

  const filterVoices = (voiceModalsArray) => {
    let uniqueVoices = {};
    voiceModalsArray.forEach((obj) => {
      uniqueVoices[obj.CategoryLabel] = obj;
    });

    // Define custom sort order
    const customOrder = ["Modal - D", "Modal - E", "Modal - C"];

    // Sort based on the custom order
    let voices = Object.values(uniqueVoices).sort((a, b) => {
      return (
        customOrder.indexOf(a.CategoryLabel) -
        customOrder.indexOf(b.CategoryLabel)
      );
    });

    setVoices(voices);
  };

  const getVoiceCategory = (voiceProvider) =>
    voiceProvider == "Eleven Labs"
      ? "Modal - E"
      : voiceProvider == "Deepgram"
        ? "Modal - D"
        : "Modal - C";

  const getDefaultVoiceModalValue = (
    voiceProvider = DEFAULT_VOICE_PROVIDER,
    availableVoiceModals = voiceModals
  ) => {
    const category = getVoiceCategory(voiceProvider);
    const preferredVoice = availableVoiceModals.find(
      (voice) =>
        voice.CategoryLabel === category &&
        voice.SubCategoryLabel === DEFAULT_VOICE_LABEL &&
        voice.SubCategoryValue
    );

    if (preferredVoice) {
      return preferredVoice.SubCategoryValue;
    }

    const fallbackVoice = availableVoiceModals.find(
      (voice) => voice.CategoryLabel === category && voice.SubCategoryValue
    );

    return fallbackVoice ? fallbackVoice.SubCategoryValue : "";
  };

  const filterVoiceModals = () => {
    const currentSelectedVoice = getVoiceCategory(selectedVoice);
    const filterVoiceModals =
      voiceModals.length > 0
        ? voiceModals.filter(
          (voice) => voice.CategoryLabel == currentSelectedVoice
        )
        : "";
    setSelectedVoiceModals(filterVoiceModals);
  };

  const handleSelectedVoice = (e) => {
    setSelectedVoice(e.target.value);
    setSelectedVoiceModal("");
  };

  const resetForm = () => {
    setProviderId("");
    setProviderName("");
    // setProviderSpecialtyType("");
    // setAdditionalOfferedService("");
    // setAdditionalOfferedServiceDetail("");
    // setBetterServicesReason("");
    // setPatientIssues("");
    // setCustomerWebsite("");
    // setOperationalHours("");
    // setInsurancesAccepted("");
    setProviderContactNumber(outBoundContact || "");
    setPromptFiledownloadLink("");
    setPromptFile("");
    setSelectedModal(DEFAULT_MODAL);
    setSelectedVoice(DEFAULT_VOICE_PROVIDER);
    setFirstMessage("");
    setLastMessage("");
    setStatus(DEFAULT_STATUS);
    // setApiKeys("");
    setSelectedVoiceModal(getDefaultVoiceModalValue(DEFAULT_VOICE_PROVIDER));
    setSelectedTimezone("");
    setBusinessHoursDays([]);
    setBusinessHoursStart("");
    setBusinessHoursEnd("");
    setVoiceMail("");
    setWantsVoiceMail(false);
    setCacheEnabled(false);
    setGreetingInterruptible(false);
    setCacheTTL(300);
    setThinkingEnabled(false);
    setReasoningEffort(DEFAULT_REASONING_EFFORT);
    setEouMinDelay(0.3);
    setEouMaxDelay(0.8);
    setTurnDetectionMode("vad");
    setSttProvider("Deepgram");
    setElevenLabsTtsModel("eleven_flash_v2_5");
    setFluxKeyterms("");
    setMultiAgentEnabled(false);
    setAgentConfigs([]);
    setBackgroundNoise(DEFAULT_BACKGROUND_NOISE);
    setSipTerminationDynamicSeconds(DEFAULT_SIP_TERMINATION_SECONDS);
    setTransferSipTerminationDynamicSeconds(DEFAULT_TRANSFER_SIP_TERMINATION_SECONDS);
    setOutboundCallerIdSource(DEFAULT_OUTBOUND_CALLER_ID_SOURCE);
    setSelectedLanguages(DEFAULT_LANGUAGES);
    setParameters([]);
    resetInputFieldsColor();
  };

  const updateForm = (e) => {
    let pId = e.target.value;

    if (pId) {
      const selectedProvider =
        providerData.length > 0
          ? providerData.filter((provider) => provider.Id == pId)
          : "";
      const selectedProviderData = selectedProvider[0];
      const nextSelectedVoice =
        selectedProviderData.SelectedVoice || DEFAULT_VOICE_PROVIDER;
      setProviderId(selectedProviderData.Id);
      sessionStorage.setItem(LEADGEN_PROVIDER_ID_STORAGE_KEY, String(selectedProviderData.Id));
      setProviderName(selectedProviderData.ProviderName);
      // setProviderFacilityName(selectedProviderData.ProviderFacilityName);
      // setProviderSpecialtyType(selectedProviderData.ProviderSpecialtyType);
      // setAdditionalOfferedService(selectedProviderData.AdditionalOfferedService);
      // setAdditionalOfferedServiceDetail(selectedProviderData.AdditionalOfferedServiceDetail);
      // setBetterServicesReason(selectedProviderData.BetterServicesReason);
      // setPatientIssues(selectedProviderData.PatientIssues);
      // setCustomerWebsite(selectedProviderData.CustomerWebsite);
      // setOperationalHours(selectedProviderData.OperationalHours);
      // setInsurancesAccepted(selectedProviderData.InsurancesAccepted);
      setProviderContactNumber(
        outBoundContact || ""
      );
      setStatus(
        selectedProviderData.Status ? selectedProviderData.Status : DEFAULT_STATUS
      );
      setParameters(
        selectedProviderData.SummaryParameters &&
          selectedProviderData.SummaryParameters !== "" &&
          selectedProviderData.SummaryParameters !== "[]" &&
          Array.isArray(selectedProviderData.SummaryParameters)
          ? selectedProviderData.SummaryParameters
          : []
      );
      // setApiKeys(selectedProviderData.API_Keys ? selectedProviderData.API_Keys : "");
      setFirstMessage(
        selectedProviderData.FirstMessage
          ? selectedProviderData.FirstMessage
          : ""
      );
      setLastMessage(
        selectedProviderData.LastMessage ? selectedProviderData.LastMessage : ""
      );
      setSelectedModal(
        selectedProviderData.SelectedModal
          ? normalizeBedrockModelId(selectedProviderData.SelectedModal)
          : DEFAULT_MODAL
      );
      setSelectedVoice(nextSelectedVoice);
      setSelectedVoiceModal(
        selectedProviderData.SelectedVoiceModal
          ? selectedProviderData.SelectedVoiceModal
          : getDefaultVoiceModalValue(nextSelectedVoice)
      );
      setPromptFiledownloadLink(
        selectedProviderData.PromptFileUrl
          ? selectedProviderData.PromptFileUrl
          : ""
      );
      setSelectedTimezone(
        selectedProviderData.Timezone ? selectedProviderData.Timezone : ""
      );
      setBusinessHoursDays(selectedProviderData.BusinessHours?.Days || []);
      setBusinessHoursStart(
        selectedProviderData.BusinessHours?.StartTime || ""
      );
      setBusinessHoursEnd(selectedProviderData.BusinessHours?.EndTime || "");
      setVoiceMail(
        selectedProviderData.VoiceMail ? selectedProviderData.VoiceMail : ""
      );
      setWantsVoiceMail(selectedProviderData.VoiceMail && selectedProviderData.VoiceMail !== "" ? true : false);
      setCacheEnabled(
        supportsBedrockPromptCaching(
          normalizeBedrockModelId(selectedProviderData.SelectedModal)
        ) &&
          (selectedProviderData.CacheEnabled === true ||
            selectedProviderData.CacheEnabled === "true")
      );
      setGreetingInterruptible(
        selectedProviderData.GreetingInterruptible === true ||
          selectedProviderData.GreetingInterruptible === "true"
      );
      setCacheTTL(selectedProviderData.CacheTTL != null ? Number(selectedProviderData.CacheTTL) : 300);
      setThinkingEnabled(
        selectedProviderData.ThinkingEnabled === true ||
          selectedProviderData.ThinkingEnabled === "true"
      );
      setReasoningEffort(selectedProviderData.ReasoningEffort || DEFAULT_REASONING_EFFORT);
      setEouMinDelay(selectedProviderData.EouMinDelay != null ? Number(selectedProviderData.EouMinDelay) : 0.3);
      setEouMaxDelay(selectedProviderData.EouMaxDelay != null ? Number(selectedProviderData.EouMaxDelay) : 0.8);
      setTurnDetectionMode(selectedProviderData.TurnDetectionMode === "semantic" ? "semantic" : "vad");
      setSttProvider(selectedProviderData.STTProvider === "Eleven Labs" ? "Eleven Labs" : "Deepgram");
      setElevenLabsTtsModel(selectedProviderData.ElevenLabsTTSModel || "eleven_flash_v2_5");
      setFluxKeyterms(formatFluxKeyterms(selectedProviderData.FluxKeyterms));
      const agentConfigsData = selectedProviderData.AgentConfigs;
      if (agentConfigsData && agentConfigsData.length > 0) {
        const knownToolNames = apiFunctions.map((func) => func.Name);
        setMultiAgentEnabled(true);
        setAgentConfigs(
          agentConfigsData.map((cfg) => ({
            ...cfg,
            tools: Array.isArray(cfg.tools)
              ? cfg.tools.filter((tool) => knownToolNames.includes(tool))
              : cfg.tools,
          }))
        );
      } else {
        setMultiAgentEnabled(false);
        setAgentConfigs([]);
      }
      setBackgroundNoise(
        selectedProviderData.BackgroundNoise != null
          ? selectedProviderData.BackgroundNoise
          : NO_BACKGROUND_NOISE
      );
      setSipTerminationDynamicSeconds(
        selectedProviderData.SipTerminationDynamicSeconds != null
          ? String(selectedProviderData.SipTerminationDynamicSeconds)
          : DEFAULT_SIP_TERMINATION_SECONDS
      );
      setTransferSipTerminationDynamicSeconds(
        selectedProviderData.TransferSipTerminationDynamicSeconds != null
          ? String(selectedProviderData.TransferSipTerminationDynamicSeconds)
          : DEFAULT_TRANSFER_SIP_TERMINATION_SECONDS
      );
      setOutboundCallerIdSource(
        selectedProviderData.OutboundCallerIdSource === "inbound"
          ? "inbound"
          : DEFAULT_OUTBOUND_CALLER_ID_SOURCE
      );
      setSelectedLanguages(
        selectedProviderData.SelectedLanguages &&
          selectedProviderData.SelectedLanguages.length > 0
          ? normalizeSelectedLanguages(selectedProviderData.SelectedLanguages)
          : DEFAULT_LANGUAGES
      );
      resetInputFieldsColor();
    } else {
      // Creating a new assistant
      sessionStorage.removeItem(LEADGEN_PROVIDER_ID_STORAGE_KEY);
      resetForm();
    }

    // document.getElementById("submitButtonForm").removeAttribute("disabled");
  };

  const handleGetFile = async () => {
    let url = "/api/getS3File";
    let obj = {
      practiceId: PracticeId,
      providerId: providerId,
      keyType: "PromptFileUrl",
      memberListType: "LeadGenerationOnboarding",
    };
    const userData = await dateRangeRequest(token, url, obj);
    if (userData == "Token is expired, login again") {
      setIsTokenExpired(true);
    } else if (userData == "No record") {
      setPromptFiledownloadLink("");
    } else {
      window.open(userData, "_blank");
    }
  };

  // Mirrors handleGetFile above, but for one row of the multi-agent table:
  // that prompt key lives inside the AgentConfigs JSON array, not a flat
  // column, so it needs its own endpoint keyed by agent index.
  const handleGetAgentPromptFile = async (index) => {
    let url = "/api/getAgentPromptFile";
    let obj = {
      practiceId: PracticeId,
      providerId: providerId,
      memberListType: "LeadGenerationOnboarding",
      agentIndex: index,
    };
    const userData = await dateRangeRequest(token, url, obj);
    if (userData == "Token is expired, login again") {
      setIsTokenExpired(true);
    } else if (userData == "No record") {
      // Nothing to open; leave the current row display as-is.
    } else {
      window.open(userData, "_blank");
    }
  };

  const handlePromptFileChange = (e) => {
    enableElement("submitButtonForm");
    let file = e.target.files[0];
    if (file) {
      if (txtFormatChecker(file)) {
        setPromptFile(file);
      } else {
        setElementValue("dynamicPrompt", "");
        alert("Require format is not txt");
      }
    } else {
      setPromptFile("");
      // document.getElementById('dynamicPrompt').classList.add("invalid-details");
    }
  };

  const handleParameters = (index, field, value) => {
    const newParameters = [...parameters];
    newParameters[index][field] = value;
    setParameters(newParameters);
  };

  const addParameter = () => {
    setParameters([...parameters, { name: "", description: "" }]);
  };

  const removeParameter = (index) => {
    setParameters(parameters.filter((_, i) => i !== index));
  };

  const handleToggleMultiAgent = (checked) => {
    setMultiAgentEnabled(checked);
    if (!checked) setAgentConfigs([]);
  };

  const addAgentConfig = () => {
    setAgentConfigs([...agentConfigs, { intent: "", promptUrl: "" }]);
  };

  const removeAgentConfig = (index) => {
    setAgentConfigs(agentConfigs.filter((_, i) => i !== index));
  };

  const handleAgentConfigChange = (index, field, value) => {
    const updated = [...agentConfigs];
    updated[index][field] = value;
    setAgentConfigs(updated);
  };

  // Empty selection gives the agent NO backend API tools (e.g. triage, which
  // only needs handoff tools); leaving this field untouched (key absent)
  // instead gives the agent every backend tool - see
  // agents/intent_agent.py filter_functions in the LiveKit repo.
  const handleAgentToolsChange = (index, tools) => {
    const updated = [...agentConfigs];
    updated[index].tools = tools;
    setAgentConfigs(updated);
  };

  const handleAgentPromptFileChange = (index, e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!txtFormatChecker(file)) {
      e.target.value = "";
      alert("Required format is not txt");
      return;
    }
    const updated = [...agentConfigs];
    updated[index].promptFile = file;
    updated[index].promptUrl = "";
    setAgentConfigs(updated);
  };

  const handleSubmit = async (e) => {
    setLoading(true);
    e.target.disabled = true;

    document.querySelectorAll(".px-3").forEach((e) => {
      const selected_Languages = document.querySelector(
        ".autocomplete_languages"
      );

      if (e.value === "" && e.id !== "selected_bgNoise") {
        e.classList.add("invalid-details");
      }

      if (selectedLanguages.length === 0 && selected_Languages) {
        selected_Languages.classList.add("invalid-details");
      }
    });

    const parametersFilled = parameters.every(
      (param) => param.name.trim() !== "" && param.description.trim() !== ""
    );

    const hasMissingRequiredFields =
      providerName === "" ||
      providerFacilityName === "" ||
      firstMessage === "" ||
      lastMessage === "" ||
      status === "" ||
      !parametersFilled ||
      selectedModal === "" ||
      selectedVoiceModal === "" ||
      selectedVoice === "" ||
      selectedTimezone === "" ||
      (wantsVoiceMail && voiceMail === "") ||
      selectedLanguages.length === 0 ||
      (!outBoundContact || outBoundContact === "") ||
      backgroundNoise === BACKGROUND_NOISE_PLACEHOLDER;
    isAnyUploadFieldEmpty = hasMissingRequiredFields;

    if (
      outBoundContact !== "" &&
      isValidContactNumber(outBoundContact) === false
    ) {
      addElementClass("ProviderContactNumber", "invalid-details");
      isValidDetails = true;
    }

    if (isAnyUploadFieldEmpty === true) {
      alert("Please enter complete details of onboarding");
      enableElement("submitButtonForm");
      setLoading(false);
      return;
    }

    if (isValidDetails === true) {
      enableElement("submitButtonForm");
      setLoading(false);
      return;
    }

    // SIP Trunk details removed as they are handled dynamically by the backend

    // let obj = {
    // PracticeId: PracticeId,
    // ProviderName: ProviderName,
    // ProviderFacilityName: ProviderFacilityName,
    // ProviderSpecialtyType: ProviderSpecialtyType,
    // AdditionalOfferedService: AdditionalOfferedService,
    // AdditionalOfferedServiceDetail: AdditionalOfferedServiceDetail,
    // BetterServicesReason: BetterServicesReason,
    // PatientIssues: PatientIssues,
    // CustomerWebsite: CustomerWebsite,
    // OperationalHours: OperationalHours,
    // InsurancesAccepted: InsurancesAccepted
    // }

    // Using form data for sending the choose files from front end to the server
    formData.append("PromptFile", promptFile);

    if (promptFile !== "") {
      formData.append("PromptFileUrl", "");
    } else {
      formData.append("PromptFileUrl", promptFiledownloadLink);
    }

    formData.append("PracticeId", PracticeId);
    formData.append("ProviderId", providerId);
    formData.append("ProviderName", providerName);
    formData.append("ProviderFacilityName", providerFacilityName);
    formData.append("ProviderContactNumber", providerContactNumber);
    formData.append("phoneNumberSid", selectedNumberSid);
    formData.append("ProviderSpecialtyType", null);
    formData.append("AdditionalOfferedService", null);
    formData.append("AdditionalOfferedServiceDetail", null);
    formData.append("BetterServicesReason", null);
    formData.append("PatientIssues", null);
    formData.append("CustomerWebsite", null);
    formData.append("OperationalHours", null);
    formData.append("InsurancesAccepted", null);
    formData.append("FirstMessage", firstMessage);
    formData.append("LastMessage", lastMessage);
    formData.append("SelectedModal", normalizeBedrockModelId(selectedModal));
    formData.append("SelectedVoiceModal", selectedVoiceModal);
    formData.append("SelectedVoice", selectedVoice);
    formData.append("Status", status);
    formData.append("Parameters", JSON.stringify(parameters));
    formData.append("API_Keys", null);
    formData.append("Timezone", selectedTimezone);
    formData.append(
      "BusinessHours",
      businessHoursDays.length > 0 && businessHoursStart && businessHoursEnd
        ? JSON.stringify({
            Days: businessHoursDays,
            StartTime: businessHoursStart,
            EndTime: businessHoursEnd,
          })
        : ""
    );
    formData.append("VoiceMail", wantsVoiceMail ? voiceMail : "");
    formData.append(
      "CacheEnabled",
      isBedrockCacheAvailable ? cacheEnabled : false
    );
    formData.append("GreetingInterruptible", greetingInterruptible);
    formData.append("CacheTTL", isBedrockCacheAvailable && cacheEnabled ? cacheTTL : 300);
    const reasoning = normalizeReasoningSelection(selectedModal, thinkingEnabled, reasoningEffort);
    formData.append("ThinkingEnabled", reasoning.thinkingEnabled);
    formData.append("ReasoningEffort", reasoning.reasoningEffort);
    formData.append("EouMinDelay", eouMinDelay);
    formData.append("EouMaxDelay", eouMaxDelay);
    formData.append("TurnDetectionMode", turnDetectionMode);
    formData.append("STTProvider", sttProvider);
    formData.append("ElevenLabsTTSModel", elevenLabsTtsModel);
    formData.append("FluxKeyterms", fluxKeyterms);
    formData.append(
      "BackgroundNoise",
      backgroundNoise === BACKGROUND_NOISE_PLACEHOLDER
        ? NO_BACKGROUND_NOISE
        : backgroundNoise
    );
    formData.append("SipTerminationDynamicSeconds", sipTerminationDynamicSeconds !== "" ? Number(sipTerminationDynamicSeconds) : "");
    formData.append("TransferSipTerminationDynamicSeconds", transferSipTerminationDynamicSeconds !== "" ? Number(transferSipTerminationDynamicSeconds) : "");
    formData.append("OutboundCallerIdSource", outboundCallerIdSource);
    formData.append("SelectedLanguages", selectedLanguages);
    if (multiAgentEnabled && agentConfigs.length > 0) {
      // Spread keeps extra keys set outside this form; promptFile (local File
      // object) is UI-only and must not be persisted. tools is ALWAYS
      // emitted: an untouched blank field must save [] (no backend tools, as
      // the column label says) - an absent key means "all backend tools" to
      // the agent.
      const configsMeta = agentConfigs.map(({ promptFile, ...cfg }) => ({
        ...cfg,
        promptUrl: cfg.promptUrl || "",
        tools: Array.isArray(cfg.tools) ? cfg.tools : [],
      }));
      formData.append("AgentConfigs", JSON.stringify(configsMeta));
      agentConfigs.forEach((cfg, i) => {
        if (cfg.promptFile) formData.append(`AgentPromptFile_${i}`, cfg.promptFile);
      });
    } else {
      formData.append("AgentConfigs", "");
    }

    let url = "/api/LeadGenerationOnboarding";
    const userData = await usersPostRequest(token, url, formData);
    if (userData == "Token is expired, login again") {
      setIsTokenExpired(true);
    } else if (userData && userData.message) {
      setLoading(false);
      alert(userData.message);
      fetchApiHandle();
    }

    setProviderId("");
    sessionStorage.removeItem(LEADGEN_PROVIDER_ID_STORAGE_KEY);
    setProviderName("");
    setProviderContactNumber(outBoundContact || "");
    // setProviderSpecialtyType("");
    // setAdditionalOfferedService("");
    // setAdditionalOfferedServiceDetail("");
    // setBetterServicesReason("");
    // setPatientIssues("");
    // setCustomerWebsite("");
    // setOperationalHours("");
    // setInsurancesAccepted("");
    setFirstMessage("");
    setLastMessage("");
    setStatus(DEFAULT_STATUS);
    setParameters([]);
    // setApiKeys("");
    setSelectedTimezone("");
    setBusinessHoursDays([]);
    setBusinessHoursStart("");
    setBusinessHoursEnd("");
    setVoiceMail("");
    setWantsVoiceMail(false);
    setCacheEnabled(false);
    setGreetingInterruptible(false);
    setCacheTTL(300);
    setThinkingEnabled(false);
    setReasoningEffort(DEFAULT_REASONING_EFFORT);
    setEouMinDelay(0.3);
    setEouMaxDelay(0.8);
    setTurnDetectionMode("vad");
    setSttProvider("Deepgram");
    setElevenLabsTtsModel("eleven_flash_v2_5");
    setFluxKeyterms("");
    setMultiAgentEnabled(false);
    setAgentConfigs([]);
    setBackgroundNoise(DEFAULT_BACKGROUND_NOISE);
    setSipTerminationDynamicSeconds(DEFAULT_SIP_TERMINATION_SECONDS);
    setTransferSipTerminationDynamicSeconds(DEFAULT_TRANSFER_SIP_TERMINATION_SECONDS);
    setOutboundCallerIdSource(DEFAULT_OUTBOUND_CALLER_ID_SOURCE);
    setSelectedLanguages(DEFAULT_LANGUAGES);
    setElementValue("selected_Provider", "");

    if (userType === "admin" || userType === "client") {
      setPromptFiledownloadLink("");
      setPromptFile("");
      setSelectedModal(DEFAULT_MODAL);
      setSelectedVoice(DEFAULT_VOICE_PROVIDER);
      setSelectedVoiceModal(getDefaultVoiceModalValue(DEFAULT_VOICE_PROVIDER));
      setElementValue("dynamicPrompt", "");
      setElementValue("selected_modal", DEFAULT_MODAL);
      setElementValue("selected_voice", DEFAULT_VOICE_PROVIDER);
      setElementValue(
        "selected_voiceModal",
        getDefaultVoiceModalValue(DEFAULT_VOICE_PROVIDER)
      );
      setElementValue("selected_timezone", "");
      setElementValue("selected_bgNoise", DEFAULT_BACKGROUND_NOISE);
    }
  };

  const handleProviderContact = (e) => {
    if (isNumber.test(e.target.value)) {
      setProviderContactNumber(e.target.value);
    }
  };

  const handleInputValues = (e) => {
    if (e.target.value !== "" || e.target.value === "") {
      enableElement("submitButtonForm");
    }
  };

  useEffect(() => {
    if (selectedVoice) {
      filterVoiceModals();
      const matchingVoiceModalValues = voiceModals
        .filter((voice) => voice.CategoryLabel === getVoiceCategory(selectedVoice))
        .map((voice) => voice.SubCategoryValue);
      const defaultVoiceModal = getDefaultVoiceModalValue(selectedVoice);

      if (
        defaultVoiceModal &&
        (!selectedVoiceModal ||
          !matchingVoiceModalValues.includes(selectedVoiceModal))
      ) {
        setSelectedVoiceModal(defaultVoiceModal);
      }
    } else {
      setSelectedVoiceModals([]);
    }
  }, [selectedVoice, selectedVoiceModal, voiceModals]);

  useEffect(() => {
    if (activeTab || PracticeId) {
      fetchApiHandle();
      fetchVoiceAndModals();
      resetForm();
      setProviderFacilityName(practiceName);
    }
  }, [activeTab, PracticeId]);

  // After a refresh, once the provider list has loaded, re-select whichever
  // provider was being edited before instead of leaving the form on blank.
  useEffect(() => {
    if (restoredProviderRef.current) return;
    if (!providerData || providerData.length === 0) return;

    const persistedProviderId = sessionStorage.getItem(LEADGEN_PROVIDER_ID_STORAGE_KEY);
    if (persistedProviderId) {
      const match = providerData.find(
        (provider) => String(provider.Id) === persistedProviderId
      );
      if (match) {
        updateForm({ target: { value: String(match.Id) } });
        setElementValue("selected_Provider", String(match.Id));
      } else {
        sessionStorage.removeItem(LEADGEN_PROVIDER_ID_STORAGE_KEY);
      }
    }
    restoredProviderRef.current = true;
  }, [providerData]);

  // Token-expiry redirect is now handled once, centrally, in ContextApi.js - see its comment
  // for why this component's own copy of the same effect was removed.

  useEffect(() => {
    if (
      providerName !== "" ||
      providerFacilityName !== "" ||
      outBoundContact !== "" ||
      firstMessage !== "" ||
      lastMessage !== "" ||
      status !== "" ||
      promptFile !== "" ||
      selectedModal !== "" ||
      selectedVoice !== "" ||
      selectedVoiceModal !== "" ||
      selectedTimezone !== "" ||
      voiceMail !== "" ||
      backgroundNoise !== DEFAULT_BACKGROUND_NOISE ||
      selectedLanguages.length !== 0 ||
      businessHoursDays.length !== 0
    ) {
      enableElement("submitButtonForm");
    } else {
      disableElement("submitButtonForm");
    }
  }, [
    providerName,
    providerFacilityName,
    firstMessage,
    lastMessage,
    status,
    promptFile,
    selectedModal,
    selectedVoice,
    selectedVoiceModal,
    selectedTimezone,
    voiceMail,
    wantsVoiceMail,
    selectedLanguages,
    businessHoursDays,
    outBoundContact,
    backgroundNoise,
  ]);

  const nextStep = () => {
    setCurrentStep((step) => Math.min(step + 1, LEAD_PROMPT_STEPS.length));
  };

  const previousStep = () => {
    setCurrentStep((step) => Math.max(step - 1, 1));
  };

  return (
    <>
      <div className="container onboardingLayout py-4 px-4 shadow-sm">
        <div className="row p-3 col-12 col-xl-10 m-auto login-container onboarding-forms bg_dark_opacity prompt-hub-panel">
          <div className="mb-3 d-flex flex-wrap align-items-center justify-content-between gap-3">
            <h6 className="fw-bold mt-2 text-white mb-1">Outbound Prompt Hub</h6>
            <button
              type="button"
              className="simple-btn register-back-btn auth-register-actions__button--secondary"
              onClick={() => navigate("/outbound")}
            >
              Back to Console
            </button>
          </div>

          {/* This page saves straight to the live config - unlike the newer Agent Config /
              Releases pages, there is no separate draft/publish step here. */}
          <div className="alert alert-info py-2 px-3 mb-3" role="status">
            Saving here creates a new live release right away.
          </div>

          <div className="auth-stepper mb-4" aria-label="Outbound prompt hub steps">
            {LEAD_PROMPT_STEPS.map((step) => {
              const isActive = step.id === currentStep;
              const isCompleted = step.id < currentStep;

              return (
                <button
                  key={step.id}
                  type="button"
                  className={`auth-stepper__item${isActive ? " is-active" : ""}${isCompleted ? " is-complete" : ""}`}
                  onClick={() => setCurrentStep(step.id)}
                >
                  <span className="auth-stepper__dot">{step.id}</span>
                  <span className="auth-stepper__copy">
                    <span className="auth-stepper__label">{step.label}</span>
                    <span className="auth-stepper__title">{step.title}</span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="col auth-register-flow prompt-hub-flow">
            {currentStep === 1 ? (
              <section className="auth-form-section auth-step-panel">
                <div className="auth-form-section__header auth-step-panel__header">
                  <div>
                    <p className="auth-step-panel__eyebrow">Step 1</p>
                    <h3 className="auth-form-section__title">Basic Details</h3>
                  </div>
                </div>
            <div className="mb-3">
              <label className="text-white form-label">Organization Name</label>
              <input
                type="text"
                onChange={(e) => setProviderFacilityName(e.target.value)}
                disabled
                onFocus={resetErrorFieldStyle}
                className="bg-black text-white border-0 py-2 px-3 form-control"
                value={providerFacilityName}
                id="ProviderFacilityName"
                placeholder="Enter your clinic name"
              />
            </div>
            <div className="mb-3">
              <label className="text-white form-label">
                Select Assistant to edit
              </label>
              <select
                className="bg-black border-0 text-white py-2 padding_left form-select"
                id="selected_Provider"
                onChange={updateForm}
              >
                <option className="selectedProviderOption" value="">
                  Select Assistant
                </option>
                {providerData.length > 0
                  ? providerData.map((provider) => {
                    return (
                      <option
                        key={provider.Id}
                        className="selectedProviderOption"
                        value={provider.Id}
                      >
                        {provider.ProviderName}
                      </option>
                    );
                  })
                  : ""}
              </select>
            </div>
            <div className="mb-3">
              <label className="text-white form-label">Assistant Name</label>
              <input
                type="text"
                onChange={(e) => setProviderName(e.target.value)}
                value={providerName}
                onFocus={resetErrorFieldStyle}
                className="bg-black text-white border-0 py-2 px-3 form-control"
                id="ProviderName"
                placeholder="Enter your clinic name"
              />
            </div>
            <div className="mb-3">
              <label className="text-white form-label">
                System Phone Number{" "}
                {(!outBoundContact || outBoundContact === "") && (
                  <span className="text-warning small fst-italic">
                    - Not Configured (Go to Phone Settings)
                  </span>
                )}
              </label>
              <input
                id="ProviderContactNumber"
                className="bg-black text-white border-0 py-2 px-3 form-control"
                value={outBoundContact || ""}
                readOnly
                placeholder="Configured outbound number will be used"
              />
            </div>
            {/* <div className="mb-3">
                            <label className="text-white form-label">Speciality Type</label>
                            <input type="text" onChange={(e) => setProviderSpecialtyType(e.target.value)} value={providerSpecialtyType} onFocus={resetErrorFieldStyle} className="bg-black text-white border-0 py-2 px-3 form-control" id="ProviderSpecialtyType" placeholder="Cardiology" />
                        </div>
                        <div className="mb-3">
                            <label className="text-white form-label">Please explain the services offered by the Clinic</label>
                            <input type="text" onChange={(e) => setAdditionalOfferedService(e.target.value)} value={additionalOfferedService} onFocus={resetErrorFieldStyle} className="bg-black text-white border-0 py-2 px-3 form-control" id="AdditionalOfferedService" placeholder="Cardiology" />
                        </div>
                        <div className="mb-3">
                            <label className="text-white form-label">Please explain the additional services offered</label>
                            <textarea rows="4" onChange={(e) => setAdditionalOfferedServiceDetail(e.target.value)} value={additionalOfferedServiceDetail} onFocus={resetErrorFieldStyle} className="bg-black text-white border-0 py-2 px-3 form-control" placeholder="Enter your text"
                                id="AdditionalOfferedServiceDetail"></textarea>
                        </div>
                        <div className="mb-3">
                            <label className="text-white form-label">Why is your service better than others?</label>
                            <textarea rows="4" onChange={(e) => setBetterServicesReason(e.target.value)} value={betterServicesReason} onFocus={resetErrorFieldStyle} className="bg-black text-white border-0 py-2 px-3 form-control" placeholder="Enter your text"
                                id="BetterServicesReason"></textarea>
                        </div>
                        <div className="mb-3">
                            <label className="text-white form-label">Enter your Customer or Patient pain points that your Company or Clinic solves</label>
                            <input type="text" onChange={(e) => setPatientIssues(e.target.value)} value={patientIssues} onFocus={resetErrorFieldStyle} className="bg-black text-white border-0 py-2 px-3 form-control" id="PatientIssues" placeholder="Cardiology" />
                        </div>
                        <div className="mb-3">
                            <label className="text-white form-label">Customer or Internet Website</label>
                            <input type="text" onChange={(e) => setCustomerWebsite(e.target.value)} value={customerWebsite} onFocus={resetErrorFieldStyle} className="bg-black text-white border-0 py-2 px-3 form-control" id="CustomerWebsite" placeholder="CustomerWebsite" />
                        </div>
                        <div className="mb-3">
                            <label className="text-white form-label">Operational/Business Hours</label>
                            <input type="text" onChange={(e) => setOperationalHours(e.target.value)} value={operationalHours} onFocus={resetErrorFieldStyle} className="bg-black text-white border-0 py-2 px-3 form-control" id="OperationalHours" placeholder="OperationalHours" />
                        </div>
                        <div className="mb-3">
                            <label className="text-white form-label">Insurances Accepted</label>
                            <input type="text" onChange={(e) => setInsurancesAccepted(e.target.value)} value={insurancesAccepted} onFocus={resetErrorFieldStyle} className="bg-black text-white border-0 py-2 px-3 form-control" id="InsurancesAccepted" placeholder="InsurancesAccepted" />
                        </div> */}
            {/* {
                            userType === "admin" ?
                                <> */}
            <div className="mb-3">
              <label className="text-white form-label">
                Upload Dynamic Prompt File
              </label>
              <input
                type="file"
                onChange={handlePromptFileChange}
                accept={".txt"}
                className="bg-black text-white border-0 py-2 padding_left form-control"
                onFocus={resetErrorFieldStyle}
                id="dynamicPrompt"
              />
              {promptFiledownloadLink &&
                promptFiledownloadLink.startsWith("http") ? (
                <a
                  href={promptFiledownloadLink}
                  target="blank"
                  download={promptFiledownloadLink}
                  className="prompt-hub-download-link"
                >
                  View File
                </a>
              ) : (
                promptFiledownloadLink && (
                  <Link
                    className="prompt-hub-download-link"
                    onClick={() => handleGetFile("prompt")}
                  >
                    View File
                  </Link>
                )
              )}
            </div>
            <div className="mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={multiAgentEnabled}
                  onChange={(e) => handleToggleMultiAgent(e.target.checked)}
                  id="multiAgentEnabled"
                />
                <label className="form-check-label text-white" htmlFor="multiAgentEnabled">
                  Enable Multi-Agent System
                </label>
              </div>
              {multiAgentEnabled && (
                <div className="mt-3" style={{ overflowX: "auto", maxWidth: "100%" }}>
                  <table className="agent-config-table">
                    <thead>
                      <tr>
                        <th>Intent</th>
                        <th>Prompt File</th>
                        <th>Tools (blank = none)</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {agentConfigs.map((cfg, i) => (
                        <tr key={i}>
                          <td>
                            <input
                              type="text"
                              value={cfg.intent}
                              onChange={(e) => handleAgentConfigChange(i, "intent", e.target.value)}
                              className="form-control"
                              placeholder="e.g. triage"
                            />
                          </td>
                          <td>
                            <input
                              type="file"
                              accept=".txt"
                              onChange={(e) => handleAgentPromptFileChange(i, e)}
                              className="form-control"
                            />
                            {cfg.promptUrl && (
                              cfg.promptUrl.startsWith("http") ? (
                                <a
                                  href={cfg.promptUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="prompt-hub-download-link"
                                >
                                  View File
                                </a>
                              ) : (
                                <Link
                                  className="prompt-hub-download-link"
                                  onClick={() => handleGetAgentPromptFile(i)}
                                >
                                  View File
                                </Link>
                              )
                            )}
                          </td>
                          <td>
                            <ToolsMultiSelectDropdown
                              id={`agent-tools-${i}`}
                              value={cfg.tools || []}
                              onChange={(tools) => handleAgentToolsChange(i, tools)}
                              options={apiFunctions.map((func) => func.Name)}
                            />
                          </td>
                          <td>
                            <button
                              type="button"
                              onClick={() => removeAgentConfig(i)}
                              className="btn btn-sm btn-danger agent-config-table__remove-btn"
                              aria-label="Remove agent"
                              title="Remove agent"
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <button
                    type="button"
                    onClick={addAgentConfig}
                    className="btn btn-sm btn-outline-light mt-2"
                  >
                    + Add Agent
                  </button>
                </div>
              )}
            </div>
            <TimeZoneDropdown
              selectedTimezone={selectedTimezone}
              onChange={handleTimezoneChange}
            />
            <BusinessHoursPicker
              days={businessHoursDays}
              onDaysChange={setBusinessHoursDays}
              startTime={businessHoursStart}
              onStartTimeChange={setBusinessHoursStart}
              endTime={businessHoursEnd}
              onEndTimeChange={setBusinessHoursEnd}
            />
              </section>
            ) : null}

            {currentStep === 2 ? (
              <section className="auth-form-section auth-step-panel">
                <div className="auth-form-section__header auth-step-panel__header">
                  <div>
                    <p className="auth-step-panel__eyebrow">Step 2</p>
                    <h3 className="auth-form-section__title">Configuration</h3>
                  </div>
                </div>
            <div className="mb-3">
              <label className="text-white form-label">
                Select Background Noise
              </label>
              <select
                className="bg-black border-0 text-white py-2 px-3 form-select"
                id="selected_bgNoise"
                value={backgroundNoise}
                onChange={(e) => setBackgroundNoise(e.target.value)}
                onFocus={resetErrorFieldStyle}
              >
                <option
                  className="selectedProviderOption"
                  value={BACKGROUND_NOISE_PLACEHOLDER}
                  disabled
                >
                  Select background noise
                </option>
                <option className="selectedProviderOption" value="">
                  None
                </option>
                <option className="selectedProviderOption" value="keyboard">
                  Keyboard
                </option>
              </select>
            </div>
            <div className="mb-3">
              <label className="text-white form-label">Sip Termination Dynamic Seconds</label>
              <input
                type="number"
                className="bg-black border-0 text-white py-2 px-3 form-control"
                id="sip_termination_dynamic_seconds"
                value={sipTerminationDynamicSeconds}
                onChange={(e) => setSipTerminationDynamicSeconds(e.target.value)}
                placeholder="e.g. 900"
                min="1"
              />
            </div>
            <div className="mb-3">
              <label className="text-white form-label">Transfer Sip Termination Dynamic Seconds</label>
              <input
                type="number"
                className="bg-black border-0 text-white py-2 px-3 form-control"
                id="transfer_sip_termination_dynamic_seconds"
                value={transferSipTerminationDynamicSeconds}
                onChange={(e) => setTransferSipTerminationDynamicSeconds(e.target.value)}
                placeholder="e.g. 900"
                min="1"
                title="How long a call is allowed to run AFTER a transfer connects before it's automatically disconnected - separate from the Sip Termination Dynamic Seconds cap above."
              />
            </div>
            <div className="mb-3">
              <label className="text-white form-label">Outbound Caller ID</label>
              <select
                className="bg-black border-0 text-white py-2 px-3 form-select"
                id="outbound_caller_id_source"
                value={outboundCallerIdSource}
                onChange={(e) => setOutboundCallerIdSource(e.target.value)}
                onFocus={resetErrorFieldStyle}
              >
                <option
                  className="selectedProviderOption"
                  value="dedicated"
                  disabled={!practiceNumbers.dedicatedOutboundAvailable}
                >
                  Dedicated outbound number
                  {practiceNumbers.outboundNumber
                    ? ` (${practiceNumbers.outboundNumber})`
                    : " (not configured)"}
                </option>
                <option
                  className="selectedProviderOption"
                  value="inbound"
                  disabled={!practiceNumbers.inboundCallerIdAvailable}
                >
                  Practice inbound number
                  {practiceNumbers.inboundNumber
                    ? ` (${practiceNumbers.inboundNumber})`
                    : " (not configured)"}
                </option>
              </select>
            </div>
            <div className="mb-3">
              <label className="text-white form-label">Select Languages</label>
              <div>
                <MultiSelectDropdown
                  id="selected_languages"
                  value={selectedLanguages}
                  onChange={setSelectedLanguages}
                  options={optionsForLanguages}
                  placeholder="Select languages ..."
                  className="autocomplete_languages"
                  onFocus={resetErrorFieldStyle}
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="text-white form-label">Flux Keyterms</label>
              <textarea
                rows="4"
                value={fluxKeyterms}
                onChange={(e) => setFluxKeyterms(e.target.value)}
                className="bg-black text-white border-0 py-2 px-3 form-control"
                placeholder="One term or phrase per line. Used only with Deepgram Flux."
              ></textarea>
              <p
                className="text-white-50 mb-0 mt-2"
                style={{ fontSize: "0.78rem", lineHeight: 1.6 }}
              >
                Outbound calls use Flux STT by default, so these keyterms apply
                automatically during recognition.
              </p>
            </div>
            <div className="mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={wantsVoiceMail}
                  onChange={(e) => setWantsVoiceMail(e.target.checked)}
                  id="wantsVoiceMail"
                />
                <label className="form-check-label text-white" htmlFor="wantsVoiceMail">
                  Do you want voice mail message?
                </label>
              </div>
            </div>
            {wantsVoiceMail && (
              <div className="mb-3">
                <label className="text-white form-label">Voice Mail</label>
                <input
                  type="text"
                  onChange={(e) => setVoiceMail(e.target.value)}
                  value={voiceMail}
                  onFocus={resetErrorFieldStyle}
                  className="bg-black text-white border-0 py-2 px-3 form-control"
                  id="voiceMail"
                  placeholder="Enter voice mail"
                />
              </div>
            )}
            <div className="mb-3">
              <label className="text-white form-label">First Message</label>
              <input
                type="text"
                onChange={(e) => setFirstMessage(e.target.value)}
                value={firstMessage}
                onFocus={resetErrorFieldStyle}
                className="bg-black text-white border-0 py-2 px-3 form-control"
                id="firstMessage"
                placeholder="First Message"
              />
            </div>
            <div className="mb-3">
              <label className="text-white form-label">Last Message</label>
              <input
                type="text"
                onChange={(e) => setLastMessage(e.target.value)}
                value={lastMessage}
                onFocus={resetErrorFieldStyle}
                className="bg-black text-white border-0 py-2 px-3 form-control"
                id="lastMessage"
                placeholder="Last Message"
              />
            </div>
            <div className="mb-3">
              <label className="text-white form-label">Status</label>
              <input
                type="text"
                onChange={(e) => setStatus(e.target.value)}
                value={status}
                onFocus={resetErrorFieldStyle}
                className="bg-black text-white border-0 py-2 px-3 form-control"
                id="status"
                placeholder="Status"
              />
            </div>
            <div className="mb-3 text-white">
              <label className="text-white form-label">
                Summarize Parameters
              </label>

              {parameters.map((param, index) => (
                <div key={index} className="mb-2">
                  <div className="d-flex align-items-center mb-2">
                    <input
                      type="text"
                      onChange={(e) =>
                        handleParameters(index, "name", e.target.value)
                      }
                      value={param.name}
                      onFocus={resetErrorFieldStyle}
                      className="bg-black text-white border-0 px-3 py-2 form-control"
                      placeholder="Name"
                      style={{
                        paddingLeft: "1rem",
                      }}
                      id={`name${index}`}
                    />
                    <button
                      onClick={() => removeParameter(index)}
                      className="delete-btn"
                    >
                      ✕
                    </button>
                  </div>

                  <input
                    type="text"
                    onChange={(e) =>
                      handleParameters(index, "description", e.target.value)
                    }
                    value={param.description}
                    onFocus={resetErrorFieldStyle}
                    className="bg-black text-white border-0 py-2 px-3 form-control"
                    placeholder="Description"
                    id={`description${index}`}
                  />
                </div>
              ))}
              <div className="mb-3">
                <button onClick={addParameter} className="simple-btn login-btn">
                  Add Parameters +
                </button>
              </div>
            </div>
              </section>
            ) : null}

            {currentStep === 3 ? (
              <section className="auth-form-section auth-step-panel">
                <div className="auth-form-section__header auth-step-panel__header">
                  <div>
                    <p className="auth-step-panel__eyebrow">Step 3</p>
                    <h3 className="auth-form-section__title">Model & Publish</h3>
                  </div>
                </div>
            {/* <div className="mb-3">
                                        <label className="text-white form-label">API Keys</label>
                                        <input type="text" onChange={(e) => setApiKeys(e.target.value)} value={apiKeys} onFocus={resetErrorFieldStyle} className="bg-black text-white border-0 py-2 px-3 form-control" id="apiKeys" placeholder="API Keys" />
                                    </div> */}
            <div className="mb-3">
              <label className="text-white form-label">Select Modal</label>
              <select
                className="bg-black border-0 text-white py-2 px-3 form-select"
                id="selected_modal"
                value={selectedModal}
                onChange={(e) => setSelectedModal(e.target.value)}
                onFocus={resetErrorFieldStyle}
              >
                <option className="selectedProviderOption" value="">
                  Select Modal
                </option>

                <option
                  className="selectedProviderOption"
                  value="us.anthropic.claude-3-7-sonnet-20250219-v1:0"
                >
                  Anthropic Claude 3.7 Sonnet
                </option>
                <option
                  className="selectedProviderOption"
                  value="us.anthropic.claude-haiku-4-5-20251001-v1:0"
                >
                  Anthropic Claude Haiku 4.5
                </option>
                <option
                  className="selectedProviderOption"
                  value="us.anthropic.claude-sonnet-4-6"
                >
                  Anthropic Claude Sonnet 4.6
                </option>
                <option
                  className="selectedProviderOption"
                  value="us.anthropic.claude-sonnet-5"
                >
                  Anthropic Claude Sonnet 5
                </option>
                <option
                  className="selectedProviderOption"
                  value="us.anthropic.claude-sonnet-5-5"
                >
                  Anthropic Claude Sonnet 5.5
                </option>
                <option
                  className="selectedProviderOption"
                  value="us.anthropic.claude-haiku-5-5"
                >
                  Anthropic Claude Haiku 5.5
                </option>
                <option
                  className="selectedProviderOption"
                  value="us.xai.grok-4.7"
                >
                  xAI Grok 4.7
                </option>
                <option
                  className="selectedProviderOption"
                  value="us.amazon.nova-lite-v1:0"
                >
                  Amazon Nova Lite
                </option>
                <option
                  className="selectedProviderOption"
                  value="us.amazon.nova-pro-v1:0"
                >
                  Amazon Nova Pro
                </option>
                <option
                  className="selectedProviderOption"
                  value="groq/meta-llama/llama-4-maverick-17b-128e-instruct"
                >
                  Allyzent 70B
                </option>
                <option
                  className="selectedProviderOption"
                  value="openai/gpt-4.1-mini"
                >
                  G-4.1 mini
                </option>
                <option
                  className="selectedProviderOption"
                  value="openai/gpt-4o-mini"
                >
                  G-4o mini
                </option>
                <option
                  className="selectedProviderOption"
                  value="bedrock-mantle/openai.gpt-5.6-terra"
                >
                  GPT-5.6 Terra
                </option>
                <option
                  className="selectedProviderOption"
                  value="bedrock-mantle/openai.gpt-5.6-luna"
                >
                  GPT-5.6 Luna
                </option>
              </select>
            </div>
            <LlmReasoningControls
              idPrefix="outbound"
              model={selectedModal}
              thinkingEnabled={thinkingEnabled}
              onThinkingChange={setThinkingEnabled}
              reasoningEffort={reasoningEffort}
              onEffortChange={setReasoningEffort}
            />
            <div className="mb-3">
              {isBedrockCacheAvailable ? (
                <>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={cacheEnabled}
                      onChange={(e) => setCacheEnabled(e.target.checked)}
                      id="cacheEnabled"
                    />
                    <label
                      className="form-check-label text-white"
                      htmlFor="cacheEnabled"
                    >
                      Enable Bedrock prompt caching
                    </label>
                  </div>
                  {cacheEnabled && (
                    <div className="mt-2">
                      <label className="text-white-50 d-block mb-1" style={{ fontSize: "0.8rem" }}>
                        Cache TTL
                      </label>
                      <div className="d-flex gap-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            id="cacheTTL5m"
                            name="cacheTTL"
                            value={300}
                            checked={cacheTTL === 300}
                            onChange={() => setCacheTTL(300)}
                          />
                          <label className="form-check-label text-white-50" htmlFor="cacheTTL5m" style={{ fontSize: "0.8rem" }}>
                            5 minutes (default)
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            id="cacheTTL1h"
                            name="cacheTTL"
                            value={3600}
                            checked={cacheTTL === 3600}
                            onChange={() => setCacheTTL(3600)}
                          />
                          <label className="form-check-label text-white-50" htmlFor="cacheTTL1h" style={{ fontSize: "0.8rem" }}>
                            1 hour (extended)
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                  <p
                    className="text-white-50 mb-0 mt-2"
                    style={{ fontSize: "0.8rem", lineHeight: 1.6 }}
                  >
                    Available for Claude Haiku 4.5 / 5.5 and Claude Sonnet
                    4.6 / 5 / 5.5 only. This caches repeated system prompt
                    and tool-definition tokens.
                  </p>
                </>
              ) : (
                <p
                  className="text-white-50 mb-0"
                  style={{ fontSize: "0.8rem", lineHeight: 1.6 }}
                >
                  {selectedModal.includes("grok")
                    ? "Grok 4.7 caches repeated prompt tokens automatically; there is nothing to configure."
                    : "Bedrock prompt caching becomes available when you select Claude Haiku 4.5 / 5.5 or Claude Sonnet 4.6 / 5 / 5.5."}
                </p>
              )}
            </div>
            <div className="mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={greetingInterruptible}
                  onChange={(e) => setGreetingInterruptible(e.target.checked)}
                  id="greetingInterruptible"
                />
                <label className="form-check-label text-white" htmlFor="greetingInterruptible">
                  Allow callers to interrupt the opening greeting
                </label>
              </div>
            </div>
            <div className="mb-3">
              <label className="text-white form-label">End-of-Utterance Delays</label>
              <div className="d-flex gap-3">
                <div className="flex-fill">
                  <label className="text-white-50 d-block mb-1" style={{ fontSize: "0.8rem" }}>
                    Min delay (seconds)
                  </label>
                  <input
                    type="number"
                    className="bg-black border-0 text-white py-2 px-3 form-control"
                    min="0.1"
                    max="2.0"
                    step="0.1"
                    value={eouMinDelay}
                    onChange={(e) => setEouMinDelay(Number(e.target.value))}
                  />
                </div>
                <div className="flex-fill">
                  <label className="text-white-50 d-block mb-1" style={{ fontSize: "0.8rem" }}>
                    Max delay (seconds)
                  </label>
                  <input
                    type="number"
                    className="bg-black border-0 text-white py-2 px-3 form-control"
                    min="1.0"
                    max="8.0"
                    step="0.1"
                    value={eouMaxDelay}
                    onChange={(e) => setEouMaxDelay(Number(e.target.value))}
                  />
                </div>
              </div>
              <p className="text-white-50 mb-0 mt-2" style={{ fontSize: "0.8rem", lineHeight: 1.6 }}>
                Min: how long to wait after silence before treating it as end-of-turn.
                Max: hard ceiling before the agent always responds.
              </p>
            </div>
            <div className="mb-3">
              <label className="text-white form-label">Turn Detection</label>
              <select
                className="bg-black border-0 text-white py-2 px-3 form-select"
                id="turn_detection_mode"
                value={turnDetectionMode}
                onChange={(e) => setTurnDetectionMode(e.target.value)}
                onFocus={resetErrorFieldStyle}
              >
                <option className="selectedProviderOption" value="vad">
                  Voice activity only (default)
                </option>
                <option className="selectedProviderOption" value="semantic">
                  Semantic model (English/multilingual auto-selected by language)
                </option>
              </select>
              <p className="text-white-50 mb-0 mt-2" style={{ fontSize: "0.8rem", lineHeight: 1.6 }}>
                Semantic layers a linguistic completion check on top of voice activity to
                judge end-of-turn more accurately - auto-picks English or multilingual model
                based on this provider's selected language(s).
              </p>
            </div>
            <div className="mb-3">
              <label className="text-white form-label">STT Provider</label>
              <select
                className="bg-black border-0 text-white py-2 px-3 form-select"
                id="stt_provider"
                value={sttProvider}
                onChange={(e) => setSttProvider(e.target.value)}
                onFocus={resetErrorFieldStyle}
              >
                <option className="selectedProviderOption" value="Deepgram">
                  Deepgram
                </option>
                <option className="selectedProviderOption" value="Eleven Labs">
                  Eleven Labs
                </option>
              </select>
              {sttProvider === "Eleven Labs" ? (
                <p
                  className="text-white-50 mb-0 mt-2"
                  style={{ fontSize: "0.8rem", lineHeight: 1.6 }}
                >
                  Uses ElevenLabs Scribe v2 Realtime - the only ElevenLabs STT model that
                  supports live streaming and works with adaptive interruption detection.
                </p>
              ) : null}
            </div>
            <div className="mb-3">
              <label className="text-white form-label">Voice Provider</label>
              <select
                className="bg-black border-0 text-white py-2 px-3 form-select"
                id="selected_voice"
                value={selectedVoice}
                onChange={handleSelectedVoice}
                onFocus={resetErrorFieldStyle}
              >
                <option className="selectedProviderOption" value="">
                  Select a voice provider
                </option>
                {voices.length > 0
                  ? voices.map((voice, index) => {
                    return (
                      <option
                        key={index}
                        className="selectedProviderOption"
                        value={voice.CategoryValue}
                      >
                        {voice.CategoryValue}
                      </option>
                    );
                  })
                  : ""}
              </select>
            </div>
            <div className="mb-3">
              <label className="text-white form-label">
                Voice
              </label>
              <select
                className="bg-black border-0 text-white py-2 px-3 form-select"
                id="selected_voiceModal"
                value={selectedVoiceModal}
                onChange={(e) => setSelectedVoiceModal(e.target.value)}
                onFocus={resetErrorFieldStyle}
              >
                <option className="selectedProviderOption" value="">
                  Select a voice
                </option>
                {/* {
                                        selectedVoice && selectedVoice === "Deepgram" ?
                                            deepgramVoiceModals.length > 0 ? deepgramVoiceModals.map(voiceModal => {
                                                return <option className="selectedProviderOption" value={voiceModal.value} >{voiceModal.label}</option>
                                            }) : ""
                                            : selectedVoice && selectedVoice === "Eleven Labs" ?
                                                elevenLabsVoiceModals.length > 0 ? elevenLabsVoiceModals.map(voiceModal => {
                                                    return <option className="selectedProviderOption" value={voiceModal.value} >{voiceModal.label}</option>
                                                }) : ""
                                                : selectedVoice && selectedVoice === "Play HT" ?
                                                    playHTVoiceModals.length > 0 ? playHTVoiceModals.map(voiceModal => {
                                                        return <option className="selectedProviderOption" value={voiceModal.value} >{voiceModal.label}</option>
                                                    }) : ""
                                                    : ""
                                    } */}
                {selectedVoiceModals.length > 0
                  ? selectedVoiceModals.map((voiceModal, index) => {
                    if (
                      voiceModal.SubCategoryLabel &&
                      voiceModal.SubCategoryValue
                    ) {
                      return (
                        <option
                          key={index}
                          className="selectedProviderOption"
                          value={voiceModal.SubCategoryValue}
                        >
                          {voiceModal.SubCategoryLabel}
                        </option>
                      );
                    }
                  })
                  : ""}
              </select>
            </div>
            {selectedVoice === "Eleven Labs" ? (
              <div className="mb-3">
                <label className="text-white form-label">TTS Model</label>
                <select
                  className="bg-black border-0 text-white py-2 px-3 form-select"
                  id="eleven_labs_tts_model"
                  value={elevenLabsTtsModel}
                  onChange={(e) => setElevenLabsTtsModel(e.target.value)}
                  onFocus={resetErrorFieldStyle}
                >
                  <option className="selectedProviderOption" value="eleven_flash_v2_5">
                    Flash v2.5 (fastest, recommended)
                  </option>
                  <option className="selectedProviderOption" value="eleven_turbo_v2_5">
                    Turbo v2.5
                  </option>
                  <option className="selectedProviderOption" value="eleven_multilingual_v2">
                    Multilingual v2 (most expressive, higher latency)
                  </option>
                </select>
                <p
                  className="text-white-50 mb-0 mt-2"
                  style={{ fontSize: "0.78rem", lineHeight: 1.6 }}
                >
                  Flash and Turbo cost the same and sound nearly identical - Flash just
                  responds faster. Multilingual v2 trades some speed for more expressive,
                  emotionally nuanced speech.
                </p>
              </div>
            ) : null}
            <div className="auth-review-grid mt-4">
              <div className="auth-review-card">
                <p className="auth-review-card__title">Assistant details</p>
                <dl className="auth-review-list">
                  <div className="auth-review-list__row">
                    <dt>Organization</dt>
                    <dd>{providerFacilityName || "Not added"}</dd>
                  </div>
                  <div className="auth-review-list__row">
                    <dt>Assistant</dt>
                    <dd>{providerName || "Not added"}</dd>
                  </div>
                  <div className="auth-review-list__row">
                    <dt>Phone</dt>
                    <dd>{outBoundContact || "Not configured"}</dd>
                  </div>
                  <div className="auth-review-list__row">
                    <dt>Timezone</dt>
                    <dd>{selectedTimezone || "Not selected"}</dd>
                  </div>
                </dl>
              </div>
              <div className="auth-review-card">
                <p className="auth-review-card__title">Prompt configuration</p>
                <dl className="auth-review-list">
                  <div className="auth-review-list__row">
                    <dt>Model</dt>
                    <dd>{selectedModal || "Not selected"}</dd>
                  </div>
                  <div className="auth-review-list__row">
                    <dt>Voice</dt>
                    <dd>{selectedVoice || "Not selected"}</dd>
                  </div>
                  <div className="auth-review-list__row">
                    <dt>Voice modal</dt>
                    <dd>{selectedVoiceModal || "Not selected"}</dd>
                  </div>
                  <div className="auth-review-list__row">
                    <dt>Prompt file</dt>
                    <dd>{promptFile?.name || promptFiledownloadLink || "Not added"}</dd>
                  </div>
                </dl>
              </div>
            </div>
              </section>
            ) : null}

            <div className="login-action-stack auth-register-actions">
              <div className="auth-register-actions__row">
                {currentStep > 1 ? (
                  <button
                    type="button"
                    className="simple-btn register-back-btn auth-register-actions__button auth-register-actions__button--secondary"
                    onClick={previousStep}
                  >
                    Back
                  </button>
                ) : (
                  <span
                    className="auth-register-actions__button prompt-hub-action-spacer"
                    aria-hidden="true"
                  />
                )}
                <div style={{ display: "flex", gap: "0.5rem" }}>
                {providerId && currentStep < LEAD_PROMPT_STEPS.length ? (
                  <button
                    type="button"
                    className="simple-btn register-back-btn auth-register-actions__button auth-register-actions__button--secondary"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    Save changes
                  </button>
                ) : null}
                {currentStep < LEAD_PROMPT_STEPS.length ? (
                  <button
                    type="button"
                    className="simple-btn login-btn form-hidden login-submit-btn auth-register-actions__button"
                    onClick={nextStep}
                  >
                    Next
                  </button>
                ) : (
                  <button
                    id="submitButtonForm"
                    type="button"
                    className="simple-btn login-btn form-hidden login-submit-btn auth-register-actions__button"
                    onClick={handleSubmit}
                  >
                    {loading ? (
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    ) : providerId ? (
                      "Save changes"
                    ) : (
                      "Publish"
                    )}
                  </button>
                )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

